import React, { useState, useEffect, useCallback } from "react";
import {
  StopConfig,
  MetroStation,
  CabiStation,
  BusStop,
  fetchMetroStations,
  fetchCabiStations,
  fetchBusStopsByLocation,
  fetchBusStopByCode,
} from "./api-client";

const toggle = (list: string[], id: string): string[] =>
  list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

const buildUrl = (home: StopConfig, work: StopConfig): string => {
  const params = new URLSearchParams();
  // Carry the neighborhood code forward if present
  const code = new URLSearchParams(window.location.search).get("n");
  if (code) params.set("n", code);
  if (home.busStops.length) params.set("homeBus", home.busStops.join(","));
  if (home.metroStations.length) params.set("homeMetro", home.metroStations.join(","));
  if (home.cabiStations.length) params.set("homeCabi", home.cabiStations.join(","));
  if (work.busStops.length) params.set("workBus", work.busStops.join(","));
  if (work.metroStations.length) params.set("workMetro", work.metroStations.join(","));
  if (work.cabiStations.length) params.set("workCabi", work.cabiStations.join(","));
  const qs = params.toString();
  return `${window.location.origin}/${qs ? `?${qs}` : ""}`;
};


const mapUrl = (lat?: number, lon?: number): string | null => {
  if (!lat || !lon) return null;
  return `https://maps.google.com/?q=${lat},${lon}`;
};
// ── Pickers ───────────────────────────────────────────────────────────────────

const MetroPicker: React.FC<{
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ selected, onChange }) => {
  const [stations, setStations] = useState<MetroStation[]>([]);
  const [query, setQuery] = useState("");

  useEffect(() => { fetchMetroStations().then(setStations); }, []);

  const filtered = stations.filter((s) =>
    s.name.toLowerCase().includes(query.toLowerCase()) ||
    s.lines.some((l) => l.toLowerCase().includes(query.toLowerCase())),
  );

  return (
    <div className="stop-picker">
      <input
        className="stop-search"
        placeholder="Search by station name or line (e.g. Red, Brookland)…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <ul className="stop-list">
        {filtered.map((s) => (
          <li
            key={s.code}
            className={`stop-item ${selected.includes(s.code) ? "stop-item-selected" : ""}`}
            onClick={() => onChange(toggle(selected, s.code))}
          >
            <input type="checkbox" readOnly checked={selected.includes(s.code)} className="stop-checkbox" />
            <span className="stop-name">{s.name}</span>
            <span className="stop-meta">{s.lines.join(", ")} · {s.code}</span>
            {mapUrl(s.lat, s.lon) && <a className="stop-map-link" href={mapUrl(s.lat, s.lon)!} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>map</a>}
          </li>
        ))}
      </ul>
    </div>
  );
};

const distanceKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const CabiPicker: React.FC<{
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ selected, onChange }) => {
  const [stations, setStations] = useState<CabiStation[]>([]);
  const [query, setQuery] = useState("");
  const [userLocation, setUserLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => { fetchCabiStations().then(setStations); }, []);

  const findNearby = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setQuery("");
        setGeoLoading(false);
      },
      () => {
        setGeoError("Could not get your location.");
        setGeoLoading(false);
      },
    );
  }, []);

  const filtered = userLocation
    ? stations
        .filter((s) => s.lat != null && distanceKm(userLocation.lat, userLocation.lon, s.lat!, s.lon!) < 1)
        .sort((a, b) => distanceKm(userLocation.lat, userLocation.lon, a.lat!, a.lon!) - distanceKm(userLocation.lat, userLocation.lon, b.lat!, b.lon!))
    : stations.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="stop-picker">
      <div className="bus-search-row">
        <div className="bus-search-group">
          <label className="bus-search-label">Search by station name</label>
          <input
            className="stop-search"
            placeholder="Search by station name…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setUserLocation(null); }}
          />
        </div>
        <div className="bus-search-group">
          <label className="bus-search-label">Or find stations near your current location</label>
          <button className="stop-search-btn" onClick={findNearby} disabled={geoLoading}>
            {geoLoading ? "Finding…" : "Find stations near me"}
          </button>
          {userLocation && <p className="stop-hint">Showing stations within 1 km — <a href="#" onClick={(e) => { e.preventDefault(); setUserLocation(null); }}>clear</a></p>}
          {geoError && <p className="stop-error">{geoError}</p>}
        </div>
      </div>
      <ul className="stop-list">
        {filtered.map((s) => (
          <li
            key={s.id}
            className={`stop-item ${selected.includes(s.id) ? "stop-item-selected" : ""}`}
            onClick={() => onChange(toggle(selected, s.id))}
          >
            <input type="checkbox" readOnly checked={selected.includes(s.id)} className="stop-checkbox" />
            <span className="stop-name">{s.name}</span>
            {mapUrl(s.lat, s.lon) && <a className="stop-map-link" href={mapUrl(s.lat, s.lon)!} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>map</a>}
          </li>
        ))}
      </ul>
    </div>
  );
};

const BusPicker: React.FC<{
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ selected, onChange }) => {
  const [codeQuery, setCodeQuery] = useState("");
  const [codeResult, setCodeResult] = useState<BusStop | null | "not-found">(null);
  const [nearbyStops, setNearbyStops] = useState<BusStop[]>([]);
  const [loading, setLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  const searchByCode = useCallback(async () => {
    if (!codeQuery.trim()) return;
    setLoading(true);
    const stop = await fetchBusStopByCode(codeQuery.trim());
    setCodeResult(stop ?? "not-found");
    setLoading(false);
  }, [codeQuery]);

  const findNearby = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }
    setLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const stops = await fetchBusStopsByLocation(pos.coords.latitude, pos.coords.longitude);
        setNearbyStops(stops);
        setLoading(false);
      },
      () => {
        setGeoError("Could not get your location. Try entering a stop code instead.");
        setLoading(false);
      },
    );
  }, []);

  const allResults = [...nearbyStops];
  if (codeResult && codeResult !== "not-found") {
    if (!allResults.find((s) => s.id === (codeResult as BusStop).id)) {
      allResults.unshift(codeResult as BusStop);
    }
  }

  return (
    <div className="stop-picker">
      <div className="bus-search-row">
        <div className="bus-search-group">
          <label className="bus-search-label">Enter stop code from the sign</label>
          <div className="bus-search-input-row">
            <input
              className="stop-search"
              placeholder="e.g. 1001195"
              value={codeQuery}
              onChange={(e) => setCodeQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchByCode()}
            />
            <button className="stop-search-btn" onClick={searchByCode} disabled={loading}>
              Look up
            </button>
          </div>
          {codeResult === "not-found" && <p className="stop-error">Stop not found.</p>}
        </div>

        <div className="bus-search-group">
          <label className="bus-search-label">Or find stops near your current location</label>
          <button className="stop-search-btn" onClick={findNearby} disabled={loading}>
            {loading ? "Finding…" : "Find stops near me"}
          </button>
          {geoError && <p className="stop-error">{geoError}</p>}
        </div>
      </div>

      {allResults.length > 0 && (
        <ul className="stop-list">
          {allResults.map((s) => (
            <li
              key={s.id}
              className={`stop-item ${selected.includes(s.id) ? "stop-item-selected" : ""}`}
              onClick={() => onChange(toggle(selected, s.id))}
            >
              <input type="checkbox" readOnly checked={selected.includes(s.id)} className="stop-checkbox" />
              <span className="stop-name">{s.name}</span>
              <span className="stop-meta">{s.routes.join(", ")} · {s.id}</span>
              {mapUrl(s.lat, s.lon) && <a className="stop-map-link" href={mapUrl(s.lat, s.lon)!} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>map</a>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Location section ──────────────────────────────────────────────────────────

const LocationSection: React.FC<{
  label: string;
  config: StopConfig;
  onChange: (config: StopConfig) => void;
}> = ({ label, config, onChange }) => {
  const [tab, setTab] = useState<"metro" | "bus" | "cabi">("metro");

  return (
    <section className="setup-section">
      <h2 className="setup-section-title">{label}</h2>
      <div className="setup-tabs">
        {(["metro", "bus", "cabi"] as const).map((t) => (
          <button
            key={t}
            className={`setup-tab ${tab === t ? "setup-tab-active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t === "metro" ? "Metro" : t === "bus" ? "Bus" : "Capital Bikeshare"}
            {t === "metro" && config.metroStations.length > 0 && ` (${config.metroStations.length})`}
            {t === "bus" && config.busStops.length > 0 && ` (${config.busStops.length})`}
            {t === "cabi" && config.cabiStations.length > 0 && ` (${config.cabiStations.length})`}
          </button>
        ))}
      </div>

      {tab === "metro" && (
        <MetroPicker
          selected={config.metroStations}
          onChange={(ids) => onChange({ ...config, metroStations: ids })}
        />
      )}
      {tab === "bus" && (
        <BusPicker
          selected={config.busStops}
          onChange={(ids) => onChange({ ...config, busStops: ids })}
        />
      )}
      {tab === "cabi" && (
        <CabiPicker
          selected={config.cabiStations}
          onChange={(ids) => onChange({ ...config, cabiStations: ids })}
        />
      )}
    </section>
  );
};

// ── Setup root ────────────────────────────────────────────────────────────────

export const Setup: React.FC<{
  homeConfig: StopConfig;
  workConfig: StopConfig;
}> = ({ homeConfig: initialHome, workConfig: initialWork }) => {
  const [home, setHome] = useState<StopConfig>(initialHome);
  const [work, setWork] = useState<StopConfig>(initialWork);
  const [copied, setCopied] = useState(false);

  const url = buildUrl(home, work);
  const hasAnything =
    home.busStops.length || home.metroStations.length || home.cabiStations.length ||
    work.busStops.length || work.metroStations.length || work.cabiStations.length;

  const copyUrl = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="setup-container">
      <h1 className="setup-title">Set up your commute dashboard</h1>
      <p className="setup-intro">
        Pick the stops near your home and work. When you&rsquo;re done, copy your link and bookmark it.
      </p>

      <LocationSection label="Home stops" config={home} onChange={setHome} />
      <LocationSection label="Work stops" config={work} onChange={setWork} />

      {hasAnything ? (
        <div className="setup-result">
          <p className="setup-result-label">Your dashboard link:</p>
          <code className="setup-url">{url}</code>
          <div className="setup-result-actions">
            <button className="setup-copy-btn" onClick={copyUrl}>
              {copied ? "Copied!" : "Copy link"}
            </button>
            <a className="setup-go-btn" href={url}>Go to dashboard →</a>
          </div>
          <p className="setup-tip">
            <strong>Tip:</strong> Add your dashboard link as a webapp to your home screen for quick access. 
            <strong>iOS (Safari):</strong> tap the Share button, then “Add to Home Screen.” 
            <strong>Android (Chrome):</strong> tap the three-dot menu, then “Add to Home Screen.”
          </p>
        </div>
      ) : (
        <p className="setup-empty">Select at least one stop above to generate your link.</p>
      )}
    </div>
  );
};
